import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { Box, Button, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { DEFAULT_WELCOME_MESSAGE, DEFAULT_UNAVAILABLE_MESSAGE } from 'constants/data';
import { FormInput, FormMediaUpload, FormSelector, Info, MyRoomLink, Spinner } from 'components';
import { useAppDispatch, useAppSelector } from 'store';
import { getUser, selectUser } from 'store/user';
import { CheckboxCheckedIcon, CheckboxIcon } from 'assets';
import { ApiUser, UpdateClinicRequest, UpdateClinicUserRequest, UpdateUserRequest } from 'types';
import { IApiRequests, useApiRequests, useAzureAsset } from 'hooks';
import {
  getIsAdminByRoles,
  getSuffixesOptionsForUser,
  getTitlesOptionsForUser,
  isPreferredDisplayNameValid,
} from 'utils';
import { useUnsavedChangesPrompt } from 'hooks';
import { isHipaaLink } from '../../../../config/productProfile';

import styles from './EditWaitingRoom.module.scss';

type EditWaitingRoomProps = {
  onDirtyChange?(dirty: boolean): void;
};

type WaitingRoomFormType = {
  user: UpdateUserRequest;
  clinicUser: UpdateClinicUserRequest;
  clinic: UpdateClinicRequest;
};

export function EditWaitingRoom({ onDirtyChange }: EditWaitingRoomProps) {
  const { t } = useTranslation('settings');
  const user: ApiUser = useAppSelector(selectUser)!;
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const ApiRequests: IApiRequests = useApiRequests();

  const [profilePicture, setProfilePicture] = React.useState<File | null>(null);
  const [profilePictureChanged, setProfilePictureChanged] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const titleSuffixMenuProps = {
    PaperProps: {
      sx: (theme: Theme) => ({
        '& .MuiMenuItem-root:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
        },
        '& .MuiMenuItem-root.Mui-selected': {
          backgroundColor: alpha(theme.palette.primary.main, 0.14),
        },
        '& .MuiMenuItem-root.Mui-selected:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.2),
        },
      }),
    },
  };
  const [isDirty, setIsDirty] = useState(false);
  const hasUnsavedChanges = isDirty || profilePictureChanged;

  // hidden temporary
  // const [logo, setLogo] = React.useState<File | null>(null);
  //const [deleteLogo, setDeleteLogo] = React.useState<boolean>(false);

  const rawAvatar = useAzureAsset(user?.linkToProfilePicture);
  const avatarUrl = rawAvatar?.startsWith('http') || rawAvatar?.startsWith('data:') ? rawAvatar : '';
  // hidden temporary
  // const logoUrl = useAzureAsset(user?.ClinicUser?.Clinic?.LogoRoute);

  const isAdmin = useMemo<boolean>(() => {
    return getIsAdminByRoles(user.clinicUser.roles || []);
  }, [user]);

  useUnsavedChangesPrompt(hasUnsavedChanges, t('unsavedChangesPrompt'));

  useEffect(() => {
    onDirtyChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onDirtyChange]);

  useEffect(() => {
    return () => {
      onDirtyChange?.(false);
    };
  }, [onDirtyChange]);

  const formDefaultValues = useMemo(
    () => ({
      clinicUser: {
        welcomeMessage: user.clinicUser.welcomeMessage ?? '',
        unavailableMessage: user.clinicUser.unavailableMessage ?? '',
        preferedDisplayName: user.clinicUser.preferedDisplayName ?? '',
        biography: user.clinicUser.biography ?? '',
        displayEmail: user.clinicUser.displayEmail,
        requirePhoneNumber: user.clinicUser.requirePhoneNumber,
        suffix: user.clinicUser.suffix ?? '',
      },
      user: {
        title: user.title ?? '',
        firstName: user.firstName,
        lastName: user.lastName,
        speciality: user.speciality ?? '',
      },
      clinic: {
        organization: user.clinicUser.organization,
      },
    }),
    [user],
  );

  const schema = useMemo(
    () =>
      Yup.object().shape({
        clinicUser: Yup.object().shape({
          welcomeMessage: Yup.string()
            .max(500, t('validationMaxLength', { count: 500 }))
            .nullable(),
          unavailableMessage: Yup.string()
            .max(100, t('validationMaxLength', { count: 100 }))
            .nullable(),
          preferedDisplayName: Yup.string()
            .max(50, t('validationMaxLength', { count: 50 }))
            .test(
              'preferred-display-name-valid',
              t('roomSettingsForm.preferredDisplayNameInvalidCharacters'),
              (value) => isPreferredDisplayNameValid(value),
            )
            .nullable(),
          biography: Yup.string()
            .max(2000, t('validationMaxLength', { count: 2000 }))
            .nullable(),
          displayEmail: Yup.boolean(),
          requirePhoneNumber: Yup.boolean(),
          suffix: Yup.string().nullable(),
        }),
        user: Yup.object().shape({
          title: Yup.string().nullable(),
          firstName: Yup.string()
            .required(t('myProfileForm.firstNameRequired'))
            .min(2, t('validationMinLength', { count: 2 }))
            .max(50, t('validationMaxLength', { count: 50 })),
          lastName: Yup.string()
            .required(t('myProfileForm.lastNameRequired'))
            .min(2, t('validationMinLength', { count: 2 }))
            .max(50, t('validationMaxLength', { count: 50 })),
          speciality: Yup.string()
            .notRequired()
            .max(25, t('validationMaxLength', { count: 25 })),
        }),
        clinic: Yup.object().shape({
          organization: Yup.string()
            .min(2, t('validationMinLength', { count: 2 }))
            .max(100, t('validationMaxLength', { count: 100 })),
        }),
      }),
    [t],
  );

  const form = useForm<WaitingRoomFormType>({
    resolver: yupResolver(schema),
    defaultValues: formDefaultValues,
    mode: 'all',
  });

  const handleDisplayEmailChange = (_: any, checked: boolean): void => {
    form.setValue('clinicUser.displayEmail', checked);
    form.trigger('clinicUser.displayEmail');
    setIsDirty(true);
  };

  const handleRequirePhoneNumberChange = (_: any, checked: boolean): void => {
    form.setValue('clinicUser.requirePhoneNumber', checked);
    form.trigger('clinicUser.requirePhoneNumber');
    setIsDirty(true);
  };

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setLoading(true);

    if (form.formState.isValid) {
      const formValues = form.getValues();

      const updateProfilePicturePromise = (): Promise<void> => {
        if (!profilePictureChanged) {
          return Promise.resolve();
        }

        return ApiRequests.updateProfilePicture(profilePicture).catch((e) =>
          Promise.reject(`Unable to update profile picture: ${e}`),
        );
      };

      const updateClinicPromise = (): Promise<void> => {
        if (isAdmin && formValues.clinic.organization !== user.clinicUser.organization) {
          return ApiRequests.updateClinic(formValues.clinic).catch((e: any) =>
            Promise.reject(`Update clinic profile failed: ${e}`),
          );
        }

        return Promise.resolve();
      };

      const updateUserPromise = (): Promise<void> => {
        return ApiRequests.updateUser(formValues.user).catch((e) => Promise.reject(`Unable to update user: ${e}`));
      };

      const updateClinicUserPromise = (): Promise<void> => {
        return ApiRequests.updateClinicUser(formValues.clinicUser).catch((e) =>
          Promise.reject(`Unable to update clinic user: ${e}`),
        );
      };

      await updateProfilePicturePromise()
        .then(() => updateClinicPromise())
        .then(() => updateUserPromise())
        .then(() => updateClinicUserPromise())
        .then(() => dispatch(getUser()).unwrap())
        .then(() => {
          setIsDirty(false);
          setProfilePictureChanged(false);
          onDirtyChange?.(false);
          enqueueSnackbar(t('roomSettingsForm.savedSuccessfully'), { variant: 'success' });
        })
        .catch((e) => enqueueSnackbar(e, { variant: 'error' }))
        .finally(() => setLoading(false));
    }
  }

  const handleLinkChange = (value?: string): void => {
    if (!value) {
      return;
    }

    setLoading(true);

    const ensureRoomNameIsUniquePromise = (): Promise<void> => {
      return ApiRequests.ensureRoomNameIsUnique(value ?? '').catch((error) => {
        return Promise.reject(error.message);
      });
    };

    ensureRoomNameIsUniquePromise()
      .then(() => ApiRequests.updateClinicUser({ roomName: value }))
      .then(() => dispatch(getUser()).unwrap())
      .then(() => enqueueSnackbar(t('roomSettingsForm.roomNameUpdated'), { variant: 'success' }))
      .catch((e) => {
        enqueueSnackbar(t('roomSettingsForm.unableToUpdateLink', { error: e }), { variant: 'error' });
      })
      .finally(() => setLoading(false));
  };

  const handlePictureChanged = (file: File): void => {
    setProfilePicture(file);
    setProfilePictureChanged(true);
  };

  const handlePictureDeleted = (): void => {
    setProfilePicture(null);
    setProfilePictureChanged(true);
  };

  useEffect(() => {
    form.reset(formDefaultValues);
  }, [formDefaultValues]);

  useEffect(() => {
    setIsDirty(form.formState.isDirty);
  }, [form.formState.isDirty]);

  const renderLabelWithInfo = (label: string, infoText: string) => (
    <Stack className={styles.labelWithInfo}>
      <Typography component='p' variant='body2'>
        {label}
      </Typography>
      <Info text={infoText} />
    </Stack>
  );

  return loading ? (
    <Spinner fixed />
  ) : (
    <>
      <Box component='form' onSubmit={handleSubmit} className={styles.form}>
        <Stack className={styles.content}>
          <Stack className={styles.row}>
            {renderLabelWithInfo(t('roomSettingsForm.roomNameUrl'), t('roomSettingsForm.roomNameUrlInfo'))}
            <MyRoomLink type='edit' value={user.clinicUser.roomName || ''} onClick={handleLinkChange} />
          </Stack>

          <Stack className={styles.row}>
            {renderLabelWithInfo(
              t('roomSettingsForm.welcomeMessage'),
              isHipaaLink ? t('roomSettingsForm.welcomeMessageInfo') : t('roomSettingsForm.welcomeMessageInfoVia3'),
            )}
            <FormInput
              name='clinicUser.welcomeMessage'
              control={form.control}
              multiline
              maxRows={4}
              placeholder={t(DEFAULT_WELCOME_MESSAGE)}
              className={styles.textareas}
              errorHelperClassName={styles.errorHelper}
            />
          </Stack>

          <Stack className={styles.row}>
            {renderLabelWithInfo(
              t('roomSettingsForm.unavailableMessage'),
              isHipaaLink
                ? t('roomSettingsForm.unavailableMessageInfo')
                : t('roomSettingsForm.unavailableMessageInfoVia3'),
            )}
            <FormInput
              name='clinicUser.unavailableMessage'
              control={form.control}
              multiline
              maxRows={4}
              placeholder={t(DEFAULT_UNAVAILABLE_MESSAGE)}
              className={styles.textareas}
              errorHelperClassName={styles.errorHelper}
            />
          </Stack>

          <div className={styles.maxWidth}>
            <Stack className={styles.row}>
              {renderLabelWithInfo(t('roomSettingsForm.profilePicture'), t('roomSettingsForm.profilePictureInfo'))}
              <FormMediaUpload
                onInputChange={handlePictureChanged}
                onRemove={handlePictureDeleted}
                title={t('roomSettingsForm.profilePicture')}
                src={avatarUrl}
              />
            </Stack>

            <Stack className={styles.row}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <Typography component='p' variant='body2'>
                  {t('myProfileForm.title')}
                </Typography>
              </Stack>
              <FormSelector
                variant='outlined'
                name='user.title'
                control={form.control}
                options={getTitlesOptionsForUser()}
                MenuProps={titleSuffixMenuProps}
              />
            </Stack>

            <Stack className={styles.row}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <Typography component='p' variant='body2'>
                  {t('myProfileForm.firstName')} <span className={styles.asterisk}>*</span>
                </Typography>
              </Stack>
              <FormInput name='user.firstName' control={form.control} errorHelperClassName={styles.errorHelper} />
            </Stack>

            <Stack className={styles.row}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <Typography component='p' variant='body2'>
                  {t('myProfileForm.lastName')} <span className={styles.asterisk}>*</span>
                </Typography>
              </Stack>
              <FormInput name='user.lastName' control={form.control} errorHelperClassName={styles.errorHelper} />
            </Stack>

            {isHipaaLink && (
              <Stack className={styles.row}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Typography component='p' variant='body2'>
                    {t('myProfileForm.suffix')}
                  </Typography>
                </Stack>
                <FormSelector
                  variant='outlined'
                  name='clinicUser.suffix'
                  control={form.control}
                  options={getSuffixesOptionsForUser()}
                  MenuProps={titleSuffixMenuProps}
                />
              </Stack>
            )}

            <Stack className={styles.row}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <Typography component='p' variant='body2'>
                  {isHipaaLink ? t('myProfileForm.specialtySpecialties') : t('myProfileForm.positionRole')}
                </Typography>
              </Stack>

              <FormInput name='user.speciality' control={form.control} errorHelperClassName={styles.errorHelper} />
            </Stack>

            <Stack className={styles.row}>
              {renderLabelWithInfo(
                t('roomSettingsForm.preferredDisplayName'),
                t('roomSettingsForm.preferredDisplayNameInfo'),
              )}
              <FormInput
                name='clinicUser.preferedDisplayName'
                control={form.control}
                errorHelperClassName={styles.errorHelper}
              />
            </Stack>

            <Stack className={styles.row}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <Typography component='p' variant='body2'>
                  {isHipaaLink
                    ? t('organizationPanel.providerOrganizationName')
                    : t('organizationPanel.providerOrganizationNameVia3')}{' '}
                  <span className={styles.asterisk}>*</span>
                </Typography>
              </Stack>
              <FormInput
                name='clinic.organization'
                disabled={!isAdmin}
                control={form.control}
                errorHelperClassName={styles.errorHelper}
              />
            </Stack>

            <Stack className={styles.row}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <Typography component='p' variant='body2'>
                  {t('roomSettingsForm.personalBiography')}
                </Typography>
              </Stack>
              <FormInput
                name='clinicUser.biography'
                className={styles.bio}
                control={form.control}
                multiline
                maxRows={20}
                placeholder={t('roomSettingsForm.personalBiography')}
                errorHelperClassName={styles.errorHelper}
              />
            </Stack>

            <Stack className={styles.row}>
              <FormControlLabel
                className={styles.displayEmailLabel}
                control={
                  <Controller
                    name='clinicUser.displayEmail'
                    control={form.control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => {
                          handleDisplayEmailChange(e, e.target.checked);
                        }}
                        icon={<CheckboxIcon className={styles.checkboxIcon} />}
                        checkedIcon={<CheckboxCheckedIcon className={styles.checkboxIcon} />}
                      />
                    )}
                  />
                }
                label={t('roomSettingsForm.displayEmailInWaitingRoom')}
              />
            </Stack>

            <Stack className={styles.row} sx={{ mt: '-1.2rem' }}>
              <FormControlLabel
                className={styles.displayEmailLabel}
                control={
                  <Controller
                    name='clinicUser.requirePhoneNumber'
                    control={form.control}
                    render={({ field }) => (
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => {
                          handleRequirePhoneNumberChange(e, e.target.checked);
                        }}
                        icon={<CheckboxIcon className={styles.checkboxIcon} />}
                        checkedIcon={<CheckboxCheckedIcon className={styles.checkboxIcon} />}
                      />
                    )}
                  />
                }
                label={t('roomSettingsForm.requirePhoneNumberDuringCheckIn')}
              />
            </Stack>
          </div>

          <Stack className={styles.row} alignItems='flex-end'>
            <Button
              variant='contained'
              onClick={handleSubmit}
              className={styles.formButton}
              type='submit'
              disabled={(!form.formState.isValid || !isDirty) && !profilePictureChanged}
            >
              {t('roomSettingsForm.save')}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </>
  );
}
